import Module from "manifold-3d";
import type { Manifold, ManifoldToplevel } from "manifold-3d";
import type { ExtractedMesh } from "@/core/mesh/surfaceNets";

/**
 * Boolean geometry via manifold-3d.
 *
 * Fixing holes are millimetre-scale features. Rasterising them into the
 * sculpting grid would mean voxels far smaller than sculpting needs — a 5.5 mm
 * hole in a 2 mm grid is mush. So holes stay analytic and are cut with a
 * genuine mesh boolean at build time. manifold-3d is a topology-guaranteeing
 * library: if it returns a result at all, that result is a closed, oriented
 * 2-manifold, which is exactly the property the STL has to have.
 *
 * If the WASM module cannot load, `subtractSolids` reports unavailability and
 * the caller falls back to rasterising the holes into the field instead — a
 * lower-fidelity hole, but still a real void, and never a failed export.
 */

let toplevel: Promise<ManifoldToplevel | null> | null = null;

async function locateWasm(): Promise<string | null> {
  if (typeof window === "undefined") return null; // node resolves it itself
  try {
    const mod = (await import("manifold-3d/manifold.wasm?url")) as { default: string };
    return mod.default;
  } catch {
    return null;
  }
}

export function loadManifold(): Promise<ManifoldToplevel | null> {
  if (!toplevel) {
    toplevel = (async () => {
      try {
        const url = await locateWasm();
        const wasm = await (url ? Module({ locateFile: () => url }) : Module());
        wasm.setup();
        return wasm;
      } catch (error) {
        console.warn("[ethos] manifold-3d unavailable, falling back to field booleans", error);
        return null;
      }
    })();
  }
  return toplevel;
}

export interface CsgResult {
  ok: boolean;
  mesh: ExtractedMesh | null;
  reason?: string;
}

function toManifold(wasm: ManifoldToplevel, mesh: ExtractedMesh): Manifold {
  const gl = new wasm.Mesh({
    numProp: 3,
    vertProperties: mesh.positions,
    triVerts: mesh.indices,
  });
  return new wasm.Manifold(gl);
}

/** Subtract every cutter from `base`. Inputs must already be closed solids. */
export async function subtractSolids(
  base: ExtractedMesh,
  cutters: readonly ExtractedMesh[],
): Promise<CsgResult> {
  const wasm = await loadManifold();
  if (!wasm) return { ok: false, mesh: null, reason: "CSG engine unavailable" };
  if (cutters.length === 0) return { ok: true, mesh: base };

  const owned: Manifold[] = [];
  try {
    const solid = toManifold(wasm, base);
    owned.push(solid);
    const status = solid.status();
    if (status !== "NoError") {
      return { ok: false, mesh: null, reason: `Sculpted mesh rejected: ${status}` };
    }

    const cutterSolids = cutters.map((c) => {
      const m = toManifold(wasm, c);
      owned.push(m);
      return m;
    });
    const tool = wasm.Manifold.union(cutterSolids);
    owned.push(tool);

    const result = solid.subtract(tool);
    owned.push(result);
    const resultStatus = result.status();
    if (resultStatus !== "NoError") {
      return { ok: false, mesh: null, reason: `Boolean failed: ${resultStatus}` };
    }

    const out = result.getMesh();
    const positions = new Float32Array(out.vertProperties.length);
    positions.set(out.vertProperties);
    const indices = new Uint32Array(out.triVerts.length);
    indices.set(out.triVerts);
    return { ok: true, mesh: { positions, indices } };
  } catch (error) {
    return {
      ok: false,
      mesh: null,
      reason: error instanceof Error ? error.message : String(error),
    };
  } finally {
    for (const m of owned) {
      try {
        m.delete();
      } catch {
        /* already released */
      }
    }
  }
}
